import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { post, postForm, get, session } from "../lib/api";
import { capture } from "../lib/analytics";
import { ConsentGate } from "../components/ConsentGate";
import { GildedFrame, PrimaryButton, Muted, ErrorText, Spinner, Header } from "../components/ui";

// The phone's half of the QR handoff. Landed here by scanning the laptop's code.
//
// This route is deliberately NOT behind the sign-in wall: the token IS the
// identity. That's the entire point — a phone that has never signed in must be
// able to shoot the selfie and have the laptop's already-paid credit found, or
// we've stranded a paying customer at a wall they paid past.
//
// The session this mints is capture-scoped (see worker/src/capture.ts): it can
// shoot and generate, and nothing else.

export function Capture() {
  const [params] = useSearchParams();
  const token = params.get("t");
  const [phase, setPhase] = useState<"redeeming" | "ready" | "working" | "done" | "error">("redeeming");
  const [packId, setPackId] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [progress, setProgress] = useState("");

  useEffect(() => {
    if (!token) {
      setErr("That link is missing its code. Please scan the QR again.");
      setPhase("error");
      return;
    }
    void (async () => {
      try {
        const res = await post<{ token: string; packId: string; sessionId: string; email: string }>(
          "/v1/capture/redeem",
          { token },
        );
        // Store the capture-scoped session like any other; the scope lives
        // server-side, so the client can't widen it by pretending otherwise.
        session.save(res.token, res.email);
        setPackId(res.packId);
        setSessionId(res.sessionId);
        setPhase("ready");
        capture("qrScanned", { packId: res.packId });
      } catch (e) {
        setErr(e instanceof Error ? e.message : "This code has expired. Please scan a fresh one.");
        setPhase("error");
      }
    })();
  }, [token]);

  async function onPhoto(file: File) {
    setPhase("working");
    setErr(null);
    try {
      await post("/v1/capture/complete", { sessionId, status: "generating" });
      setProgress("Unlocking this era…");
      await post("/v1/redeem", { packId });

      setProgress("Checking your photo…");
      const form = new FormData();
      form.append("packId", packId);
      form.append("selfie", file);
      const job = await postForm<{ jobId: string }>("/v1/generate/gallery/async", form);
      capture("webGenerationStarted", { packId, inputMethod: "qr" });

      setProgress("Painting your portraits…");
      for (;;) {
        await new Promise((r) => setTimeout(r, 2500));
        const s = await get<{ status: string; completed?: number; total?: number; error?: string }>(
          `/v1/generate/job?id=${job.jobId}`,
        );
        if (s.completed && s.total) setProgress(`Painting your portraits… ${s.completed} of ${s.total}`);
        if (s.status === "ready") {
          // Tell the laptop. The gallery is on the account either way — this
          // just releases the other device's waiting screen.
          await post("/v1/capture/complete", { sessionId, status: "ready" }).catch(() => {});
          capture("webGenerationCompleted", { packId, inputMethod: "qr" });
          setPhase("done");
          return;
        }
        if (s.status === "failed") throw new Error(s.error || "Generation failed.");
      }
    } catch (e) {
      const m = e instanceof Error ? e.message : "Something went wrong.";
      await post("/v1/capture/complete", { sessionId, status: "failed", error: m }).catch(() => {});
      capture("webGenerationFailed", { packId, inputMethod: "qr" });
      setErr(m);
      setPhase("ready");
    }
  }

  if (phase === "redeeming") return <Spinner />;

  if (phase === "error") {
    return (
      <div className="mx-auto max-w-md text-center">
        <GildedFrame>
          <div className="p-6">
            <h2 className="font-serif text-xl text-ivory">That code didn't work</h2>
            <Muted className="mt-2">{err}</Muted>
            <Muted className="mt-3 text-[12px]">
              Nothing was lost. Your credit is safe on your account, on the computer where you bought it.
            </Muted>
          </div>
        </GildedFrame>
      </div>
    );
  }

  if (phase === "done") {
    return (
      <div className="mx-auto max-w-md text-center">
        <GildedFrame>
          <div className="p-6">
            <div className="text-3xl">✓</div>
            <h2 className="mt-2 font-serif text-xl text-ivory">Your portraits are ready</h2>
            <Muted className="mt-2">
              They're on your account, so they're already waiting on the computer you bought from, and in the app if you
              have it.
            </Muted>
          </div>
        </GildedFrame>
      </div>
    );
  }

  if (phase === "working") {
    return (
      <div className="mx-auto max-w-md text-center">
        <GildedFrame>
          <div className="p-8">
            <Spinner />
            <h2 className="mt-2 font-serif text-lg text-ivory">{progress}</h2>
            <Muted className="mt-2">Keep this page open.</Muted>
          </div>
        </GildedFrame>
      </div>
    );
  }

  return (
    <ConsentGate>
      <Header kicker="Your phone" title="Take your selfie" />
      <PhoneCapture onFile={onPhoto} />
      <ErrorText>{err}</ErrorText>
    </ConsentGate>
  );
}

/**
 * The phone camera. `capture="user"` asks for the front camera directly, which is
 * the whole reason we sent them here — and the native camera UI beats anything we
 * would build in a mobile browser.
 */
function PhoneCapture({ onFile }: { onFile: (f: File) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  return (
    <div className="mx-auto max-w-sm">
      <GildedFrame>
        <div className="p-4">
          {preview ? (
            <img src={preview} alt="Your selfie" className="aspect-square w-full rounded-lg object-cover" />
          ) : (
            <div className="flex aspect-square w-full items-center justify-center rounded-lg border border-dashed border-gold/30 bg-screenInk">
              <Muted>No photo yet</Muted>
            </div>
          )}

          <div className="mt-3 space-y-1">
            <Muted className="text-[12px]">• Hold the phone at eye level, arm's length.</Muted>
            <Muted className="text-[12px]">• Face a window. Light on your face, not behind you.</Muted>
            <Muted className="text-[12px]">• Just you, looking at the lens.</Muted>
          </div>

          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            capture="user"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              setFile(f);
              setPreview(URL.createObjectURL(f));
            }}
          />

          <div className="mt-4 space-y-2">
            <PrimaryButton onClick={() => inputRef.current?.click()}>
              {preview ? "Retake" : "Open camera"}
            </PrimaryButton>
            {file && (
              <PrimaryButton onClick={() => onFile(file)} className="!bg-none !border !border-gold/40 !text-gold">
                Use this photo
              </PrimaryButton>
            )}
          </div>
        </div>
      </GildedFrame>
    </div>
  );
}
